import { FC, MutableRefObject, useEffect, useState, useCallback, useRef, useMemo } from "react"
import { View, ViewStyle, TextStyle, TouchableOpacity } from "react-native"
import { APIProvider, Map, useMap } from "@vis.gl/react-google-maps"

import Config from "@/config"
import { useApproachMoments } from "@/hooks/useApproachMoments"
import { useFollowCamera } from "@/hooks/useFollowCamera"
import { useStores } from "@/models"
import { HubLocation } from "@/services/firebase"
import { carSpriteVariant } from "@/services/map/car-sprite-variant"
import { CarTrailState, createTrailState } from "@/services/map/car-trail"
import { resolveTrackingLeg } from "@/services/map/tracking-leg"
import { colors, spacing } from "@/theme"
import { calculateDistance } from "@/utils/routeHelpers"

import { AnimatedEndpointMarker } from "./AnimatedEndpointMarker"
import { AnimatedRouteLine } from "./AnimatedRouteLine"
import { CarTrailOverlay } from "./CarTrailOverlay"
import { MarkerInfoWindow } from "./MarkerInfoWindow"
import { SmoothCarMarker } from "./SmoothCarMarker"

interface Location {
  latitude: number
  longitude: number
  timestamp?: number
  heading?: number
  speed?: number
}

interface TrackingMapViewProps {
  driverLocation: Location | null
  customerLocation: Location | null
  customerAddress?: string
  showRoute: boolean
  orderPhase?: number
  pickupLocation?: Location | null
  isDriverReturning?: boolean
  isDriveby?: boolean
  onMapLoaded?: () => void
  allLocations?: HubLocation[]
  showAllLocations?: boolean
  onTransitionToOrder?: () => void
  fleetHubCustomerLocations?: Array<{
    id: string
    latitude: number
    longitude: number
    companyName: string
    address: string
    isParent: boolean
  }>
  /**
   * Click on a FleetHub customer location marker (PSP view: filter the list by
   * that location). Optional; without it a click only logs, as on /fleethub.
   */
  onCustomerLocationPress?: (locationId: string) => void
  /**
   * Fit the initial bounds to the customer markers even without hub locations
   * (PSP view: the hub load failed). Default false: wait for hubs, as on /fleethub.
   */
  fitWithoutHubs?: boolean
  activeOrders?: Map<string, any> // ActiveOrderTrackingData from FleetHub store
  showMultipleOrders?: boolean
  bottomSheetHeight?: number // Actual measured height from bottom sheet component
  headerHeight?: number // Actual measured height from header (FleetHub only)
}

// Google Maps API key from config
const GOOGLE_MAPS_API_KEY = Config.GOOGLE_MAPS_API_KEY

/**
 * Calculate simple padding using measured bottom sheet and header heights
 * @param bottomSheetHeight - Actual measured height from bottom sheet component
 * @param headerHeight - Actual measured height from header (FleetHub only)
 * @returns Padding object for google.maps.fitBounds()
 */
const getMapPadding = (bottomSheetHeight?: number, headerHeight?: number) => {
  // Use provided heights or fallback to 0 if not available yet
  const actualBottomSheetHeight = bottomSheetHeight || 0
  const actualHeaderHeight = headerHeight || 0

  // Simple, uniform padding with actual measured heights
  return {
    top: actualHeaderHeight + 50, // Account for header overlay + buffer
    right: 50,
    bottom: actualBottomSheetHeight + 50, // Account for bottom sheet overlay + buffer
    left: 50,
  }
}

/**
 * Calculate the geographic center point of multiple customer locations
 * @param locations - Array of customer locations
 * @returns Center point as Location
 */
const calculateCenterPoint = (
  locations: Array<{ latitude: number; longitude: number }>,
): { latitude: number; longitude: number } => {
  if (locations.length === 0) {
    return { latitude: 0, longitude: 0 }
  }

  if (locations.length === 1) {
    return locations[0]
  }

  // Calculate average latitude and longitude
  const sum = locations.reduce(
    (acc, loc) => ({
      latitude: acc.latitude + loc.latitude,
      longitude: acc.longitude + loc.longitude,
    }),
    { latitude: 0, longitude: 0 },
  )

  return {
    latitude: sum.latitude / locations.length,
    longitude: sum.longitude / locations.length,
  }
}

/**
 * Filter inspection centers to show only the most relevant ones
 * Prioritizes centers with active orders, then includes only the closest center
 * @param allCenters - All available inspection centers
 * @param customerLocations - Customer location markers
 * @param activeOrders - Map of active orders (to check which centers have active orders)
 * @returns Filtered array of centers to display
 */
const filterRelevantCenters = (
  allCenters: HubLocation[],
  customerLocations: Array<{ latitude: number; longitude: number }>,
  activeOrders?: Map<string, any>,
): HubLocation[] => {
  if (allCenters.length === 0 || customerLocations.length === 0) {
    return allCenters
  }

  // Calculate center point of all customer locations
  const customerCenter = calculateCenterPoint(customerLocations)

  // Calculate distances for all centers
  const centersWithDistance = allCenters.map((center) => ({
    center,
    distance: calculateDistance(customerCenter, {
      latitude: center.latitude,
      longitude: center.longitude,
    }),
  }))

  // Separate centers with active orders from others
  const activeCenters: typeof centersWithDistance = []
  const inactiveCenters: typeof centersWithDistance = []

  centersWithDistance.forEach((item) => {
    const hasActiveOrder = activeOrders
      ? Array.from(activeOrders.values()).some(
          (order: any) => order?.hubLocation?.id === item.center.id,
        )
      : false

    if (hasActiveOrder) {
      activeCenters.push(item)
    } else {
      inactiveCenters.push(item)
    }
  })

  // Sort inactive centers by distance
  inactiveCenters.sort((a, b) => a.distance - b.distance)

  // Take active centers + 1 closest inactive center
  const relevantCenters = [...activeCenters, ...inactiveCenters.slice(0, 1)]

  console.log(
    `[FleetHub] Filtered ${allCenters.length} centers to ${relevantCenters.length} relevant ones (${activeCenters.length} active + ${Math.min(1, inactiveCenters.length)} closest)`,
  )

  return relevantCenters.map((item) => item.center)
}

// Component to handle map content
const MapContent: FC<
  TrackingMapViewProps & {
    onMapLoad: (map: google.maps.Map) => void
    orderTrackingStore: any
    selectedMarker: "hub" | "customer" | null
    setSelectedMarker: (marker: "hub" | "customer" | null) => void
    setCenterBounds: (bounds: google.maps.LatLngBounds | null) => void
    centerBounds: google.maps.LatLngBounds | null
    isMobile: boolean
    /** PLA-266: true while the follow camera owns the viewport — bounds fitting yields. */
    followCameraOwned: boolean
    /**
     * PLA-267: destination-pin (customer) pulse intensity for the approach
     * choreography; undefined = choreography inactive (pin renders as before).
     */
    customerPulseIntensity?: number
    /**
     * PLA-287: hub-pin pulse intensity for the customer -> hub ("toHub") leg,
     * when the hub becomes the active approach destination; undefined on the
     * pickup/return legs (hub renders with its static halo as before).
     */
    hubPulseIntensity?: number
    /** PLA-267 role swap: hub halo hands its role over on the return/toHub leg. */
    hubWavesVisible: boolean
    /** PLA-267: crossfade wave/role changes (false under reduced motion). */
    waveChangeAnimated: boolean
    /** PLA-267: one-shot docking-settle counter for the live car marker. */
    dockingSignal: number
    /**
     * PLA-273: true only while live-driving a single B2C order (phases 3/6
     * with a driver position) — the same gate the follow camera and route
     * shimmer key off. Gates the comet-tail trail buffer + overlay.
     */
    isLiveDriving: boolean
    /**
     * PLA-292: monotonic counter incremented when the order transitions to
     * phase 7 (completion). Triggers a one-shot confetti burst on the marker.
     */
    celebrationSignal: number
    /**
     * PLA-292: when true the single-order path renders a keep-alive marker
     * at the customer/delivery location to host the completion confetti burst.
     * Clears ~1800ms after the phase-7 transition.
     */
    justCompleted: boolean
  }
> = ({
  driverLocation,
  customerLocation: _customerLocation,
  customerAddress: _customerAddress,
  showRoute,
  orderPhase = 3,
  pickupLocation: _pickupLocation,
  isDriverReturning,
  isDriveby = false,
  onMapLoad,
  orderTrackingStore,
  selectedMarker,
  setSelectedMarker,
  allLocations = [],
  showAllLocations = false,
  onTransitionToOrder,
  fleetHubCustomerLocations = [],
  onCustomerLocationPress,
  fitWithoutHubs = false,
  setCenterBounds,
  centerBounds,
  activeOrders,
  showMultipleOrders = false,
  isMobile: _isMobile,
  bottomSheetHeight,
  headerHeight,
  followCameraOwned,
  customerPulseIntensity,
  hubPulseIntensity,
  hubWavesVisible,
  waveChangeAnimated,
  dockingSignal,
  isLiveDriving,
  celebrationSignal,
  justCompleted,
}) => {
  const map = useMap()

  // Track if initial FleetHub bounds have been set (to prevent automatic re-centering)
  const hasSetFleetHubBounds = useRef(false)

  // PLA-266: live mirror of followCameraOwned so the delayed fitMapBounds
  // timeouts (scheduled while still in overview) also yield once follow engages.
  const followOwnedRef = useRef(followCameraOwned)
  followOwnedRef.current = followCameraOwned

  // PLA-279: latest-value ref for activeOrders. The FleetHub bounds effect only
  // reads activeOrders to pick which inspection centers to show ONCE on initial
  // load — it must NOT re-subscribe to it. activeOrders is a Zustand Map that
  // gets a fresh reference on every live-driver location tick; keeping it in the
  // bounds effect's dependency array re-ran the fit-to-bounds machinery per tick,
  // recentering the camera on car movement in multi-order mode (the bug). Reading
  // it through this ref breaks that per-tick coupling without staleness on the
  // one-time read.
  const activeOrdersRef = useRef(activeOrders)
  activeOrdersRef.current = activeOrders

  // PLA-272 stale-GPS affordance: the single-order car marker reports engine
  // staleness transitions (90s without a fix); while stale the route shimmer
  // pauses. Single-order B2C only — FleetHub multi-order routes would need
  // per-order flags and are out of scope for this slice.
  const [carIsStale, setCarIsStale] = useState(false)

  // PLA-273 comet-tail trail: shared displayed-pose buffer between the live
  // car marker (writer) and the canvas overlay (reader). Single-order B2C
  // only, like the stale wiring above. The ref persists across phase changes;
  // points age out within ~2.5s so a leg change never replays an old tail.
  const carTrailRef = useRef<CarTrailState>(createTrailState())

  // FleetHub multi-order trails: the single carTrailRef above is the B2C buffer.
  // Each live FleetHub car needs its OWN comet-tail buffer, so keep a per-orderId
  // pool. A live order's SmoothCarMarker (writer) and its CarTrailOverlay
  // (reader) share the same wrapper: the marker mutates `.current`, the overlay
  // reads it. Entries persist across phase flips (the buffer drains via
  // time-decay) and are reused if the order re-enters a live phase, so the trail
  // survives a brief stop. Bounded by the fleet size; orphaned buffers just idle.
  // globalThis.Map: `Map` is imported from @vis.gl as the map COMPONENT, which
  // shadows the global Map constructor in value position (the type annotation
  // still resolves to the global Map type, like the activeOrders prop above).
  const fleetTrailRefs = useRef<Map<string, MutableRefObject<CarTrailState>>>(new globalThis.Map())
  const getFleetTrail = useCallback((orderId: string): MutableRefObject<CarTrailState> => {
    const pool = fleetTrailRefs.current
    let ref = pool.get(orderId)
    if (!ref) {
      ref = { current: createTrailState() }
      pool.set(orderId, ref)
    }
    return ref
  }, [])

  // State for FleetHub marker info windows
  const [selectedFleetHubMarker, setSelectedFleetHubMarker] = useState<{
    type: "hub" | "car"
    orderId: string
    position: { lat: number; lng: number }
    title: string
    address: string
  } | null>(null)

  // Reset bounds tracking on unmount
  useEffect(() => {
    return () => {
      hasSetFleetHubBounds.current = false
    }
  }, [])

  // Debug logging for map data
  useEffect(() => {
    console.log("[DEBUG MapContent] showAllLocations:", showAllLocations)
    console.log("[DEBUG MapContent] allLocations:", allLocations)
    console.log("[DEBUG MapContent] allLocations.length:", allLocations.length)
    console.log("[DEBUG MapContent] fleetHubCustomerLocations:", fleetHubCustomerLocations.length)
  }, [showAllLocations, allLocations, fleetHubCustomerLocations])

  // Memoize click handlers to prevent re-renders
  const handleHubClick = useCallback(() => {
    setSelectedMarker("hub")
  }, [setSelectedMarker])

  const handleCustomerClick = useCallback(() => {
    setSelectedMarker("customer")
  }, [setSelectedMarker])

  // Initialize map and directions
  useEffect(() => {
    // console.log("[MapContent] Map state:", map)
    if (map) {
      // console.log("[MapContent] Map is ready, initializing...")
      onMapLoad(map)
    }
  }, [map, onMapLoad])

  // Fit bounds to show all markers - only on initial load
  useEffect(() => {
    if (!map || !showRoute) return

    // PLA-266: while the follow camera owns the viewport (follow/paused), the
    // bounds/auto-zoom logic yields entirely — no camera fights. When the live
    // phase ends the machine lands back on overview and this effect re-runs.
    if (followCameraOwned) return

    // Function to fit map bounds
    const fitMapBounds = () => {
      // PLA-266: yield even from the delayed setTimeout fallbacks below — follow
      // mode may have engaged between scheduling and execution.
      if (followOwnedRef.current) return

      // Check if this is a driveby order or single location scenario
      const isSingleLocation =
        isDriveby ||
        (_pickupLocation && !_customerLocation) ||
        (!_pickupLocation && _customerLocation)

      if (isSingleLocation && (_pickupLocation || _customerLocation)) {
        // For single marker (e.g., driveby), use setCenter and setZoom for better display
        const centerLocation = _pickupLocation || _customerLocation

        map.setCenter({
          lat: centerLocation!.latitude,
          lng: centerLocation!.longitude,
        })
        map.setZoom(14) // Slightly more zoomed in

        // console.log("[TrackingMapView] Single location centered with zoom 15")
        return
      }

      // Multiple locations - use simple LatLngBounds.extend() approach
      const bounds = new google.maps.LatLngBounds()

      // Add pickup location if available
      if (_pickupLocation) {
        bounds.extend({ lat: _pickupLocation.latitude, lng: _pickupLocation.longitude })
      }

      // Add customer location if available (except for driveby)
      if (_customerLocation && !isDriveby) {
        bounds.extend({ lat: _customerLocation.latitude, lng: _customerLocation.longitude })
      }

      // If no locations available, don't fit bounds
      if (!_pickupLocation && !_customerLocation) {
        return // Don't fit bounds without data
      }

      // Only fit bounds if we have bottom sheet height (prevents incorrect initial bounds)
      if (!bottomSheetHeight) {
        console.log("[TrackingMapView] Waiting for bottom sheet height before fitting bounds")
        return
      }

      // Use simple padding that accounts for actual bottom sheet and header heights
      const padding = getMapPadding(bottomSheetHeight, headerHeight)

      // Fit bounds with padding
      map.fitBounds(bounds, padding)

      // console.log("[TrackingMapView] Initial fitBounds completed")
    }

    // If we have locations available, try immediate centering first
    if (_pickupLocation || _customerLocation) {
      // Try immediate centering
      fitMapBounds()
      // Also schedule a delayed centering as fallback
      setTimeout(fitMapBounds, 500)
    } else {
      // No locations yet, just schedule the delayed centering
      setTimeout(fitMapBounds, 500)
    }
  }, [
    map,
    showRoute,
    _pickupLocation,
    _customerLocation,
    isDriveby,
    bottomSheetHeight,
    headerHeight,
    followCameraOwned,
  ]) // Re-center when locations or bottom sheet height changes (yields to follow camera)

  // Handle all locations display - use simple LatLngBounds.extend() approach
  useEffect(() => {
    if (!map || !showAllLocations || (allLocations.length === 0 && !fitWithoutHubs)) return

    // CRITICAL: Only calculate bounds ONCE on initial load for FleetHub
    // This prevents automatic re-centering as data updates
    if (hasSetFleetHubBounds.current) {
      console.log("[TrackingMapView] Skipping FleetHub bounds recalculation - already set")
      return
    }

    // FleetHub Mode: Show all hub locations + all customer locations (parent + children)
    // IMPORTANT: Wait for BOTH hub and customer locations to be loaded before calculating bounds
    // This prevents race condition where bounds are calculated with only hub locations
    if (fleetHubCustomerLocations.length > 0) {
      // PLA-279: bail BEFORE doing any work (incl. setCenterBounds) until the
      // bottom sheet height is known. The fit + the centerBounds write + the
      // lock must all happen together in ONE pass; writing centerBounds early
      // (in the no-height window) churned its reference on every render and
      // re-triggered the bottom-sheet-adjust effect, recentering per tick.
      if (!bottomSheetHeight) {
        console.log(
          "[TrackingMapView] Waiting for bottom sheet height before fitting FleetHub bounds",
        )
        return
      }

      // Filter hub locations to show only relevant ones (active centers + 1 closest).
      // PLA-279: read activeOrders via ref — it only seeds the one-time relevant-
      // center pick and must not re-subscribe this effect to per-tick order updates.
      const relevantHubLocations = filterRelevantCenters(
        allLocations,
        fleetHubCustomerLocations,
        activeOrdersRef.current,
      )

      console.log(
        `[FleetHub] Using ${relevantHubLocations.length} relevant centers for bounds calculation`,
      )

      // Use simple LatLngBounds.extend() approach for filtered markers
      const bounds = new google.maps.LatLngBounds()

      // Add only relevant hub locations (not all)
      relevantHubLocations.forEach((location) => {
        bounds.extend({ lat: location.latitude, lng: location.longitude })
      })

      // Add all customer locations (parent + children)
      fleetHubCustomerLocations.forEach((location) => {
        bounds.extend({ lat: location.latitude, lng: location.longitude })
      })

      // Store bounds for recenter button (written exactly once, on lock).
      setCenterBounds(bounds)

      // Use simple padding that accounts for actual bottom sheet and header heights
      const padding = getMapPadding(bottomSheetHeight, headerHeight)

      // Fit bounds with padding
      map.fitBounds(bounds, padding)

      // Mark initial bounds as set - prevents automatic re-centering on subsequent renders
      hasSetFleetHubBounds.current = true
      console.log(
        "[TrackingMapView] Initial FleetHub bounds calculated with all locations and locked",
      )
    } else {
      // Customer locations not yet loaded - waiting for data
      console.log("[TrackingMapView] Waiting for customer locations before calculating bounds")
    }
    // Note: If fleetHubCustomerLocations is empty (OrderTracking mode),
    // the original useEffect for normal tracking will handle bounds calculation
    // PLA-279: activeOrders is intentionally NOT a dependency — see activeOrdersRef.
  }, [
    map,
    showAllLocations,
    allLocations,
    fitWithoutHubs,
    fleetHubCustomerLocations,
    setCenterBounds,
    bottomSheetHeight,
    headerHeight,
  ])

  // Adjust FleetHub bounds when bottom sheet height changes (expand/collapse)
  useEffect(() => {
    // Only run for FleetHub mode after initial bounds have been set
    if (!map || !showAllLocations || !centerBounds) return
    if (!bottomSheetHeight || !hasSetFleetHubBounds.current) return

    console.log("[TrackingMapView] Adjusting FleetHub bounds for bottom sheet height change")

    // Recalculate padding with new bottom sheet height
    const padding = getMapPadding(bottomSheetHeight, headerHeight)

    // Refit to stored bounds with updated padding
    map.fitBounds(centerBounds, padding)
  }, [bottomSheetHeight, headerHeight, map, showAllLocations, centerBounds]) // Only triggers when bottom sheet or header height changes

  // Handle transition from all locations to order view
  useEffect(() => {
    if (!showAllLocations && onTransitionToOrder) {
      // Data has loaded and we're not showing all locations - transition to order view
      // Give a small delay then call the transition handler
      setTimeout(onTransitionToOrder, 100)
    }
  }, [showAllLocations, onTransitionToOrder])

  return (
    <>
      {/* All Locations Markers - Only show when showAllLocations is true */}
      {showAllLocations &&
        allLocations.map((location) => (
          <AnimatedEndpointMarker
            key={location.id}
            position={{ lat: location.latitude, lng: location.longitude }}
            waveColor={colors.primary}
            markerContent="delivery" // HDP logo without waves
            showWaves={false}
            onClick={() => console.log(`Clicked location: ${location.location}`)}
          />
        ))}

      {/* FleetHub Customer Location Markers - Show all parent and child company locations */}
      {console.log(
        "[TrackingMapView] Rendering",
        fleetHubCustomerLocations.length,
        "customer location markers",
      )}
      {showAllLocations &&
        fleetHubCustomerLocations.length > 0 &&
        fleetHubCustomerLocations.map((location, _index) => (
          <AnimatedEndpointMarker
            key={`customer-${location.id}`}
            position={{
              lat: location.latitude,
              lng: location.longitude,
            }}
            waveColor={colors.primary}
            markerContent="pickup" // Red dot marker
            showWaves={false} // No waves for customer locations
            // Accessible name only where the click does something (PSP filter).
            title={
              onCustomerLocationPress
                ? `${location.companyName} – nach diesem Standort filtern`
                : undefined
            }
            onClick={() =>
              onCustomerLocationPress
                ? onCustomerLocationPress(location.id)
                : console.log(`Clicked customer location: ${location.companyName}`)
            }
          />
        ))}

      {/* Multiple Order Driver Markers - Show when in FleetHub multi-order mode */}
      {showMultipleOrders &&
        activeOrders &&
        (() => {
          const ordersArray = Array.from(activeOrders.values())
          const ordersWithLocation = ordersArray.filter((o) => o?.driverLocation)

          console.log(
            `[TrackingMapView] Rendering ${ordersWithLocation.length}/${ordersArray.length} car markers`,
          )

          return ordersArray.map((orderData: any) => {
            // Validate orderData
            if (!orderData || !orderData.orderId) {
              console.warn("[TrackingMapView] Invalid orderData, skipping marker")
              return null
            }

            // Skip for driveby orders
            if (orderData.serviceType === "driveby") return null

            // Phase 1-2: No car shown (order not picked up yet)
            if (orderData.orderPhase === 1 || orderData.orderPhase === 2) return null

            // Phase 4-5: Show static customer car at hub (car being inspected)
            if (orderData.orderPhase === 4 || orderData.orderPhase === 5) {
              if (!orderData.hubLocation) {
                console.log(
                  `[TrackingMapView] Order ${orderData.orderId} at hub phase but no hubLocation`,
                )
                return null
              }

              return (
                <SmoothCarMarker
                  key={orderData.orderId}
                  // Static at-hub placement: no live GPS leg exists, so the
                  // stale affordance is meaningless here. No timestamp either —
                  // a per-render Date.now() would re-ingest as a fresh fix on
                  // every render now that ingestion keys on it (Codex P2).
                  staleAffordance={false}
                  // PLA-278: at-hub inspection (phase 4/5) is the customer's car.
                  spriteVariant={carSpriteVariant(orderData.orderPhase, false)}
                  // PLA-292: scanner overlay while car is at hub for inspection.
                  scanning={true}
                  location={{
                    latitude: orderData.hubLocation.latitude,
                    longitude: orderData.hubLocation.longitude,
                    heading: 0,
                    speed: 0,
                  }}
                  onClick={() => {
                    setSelectedFleetHubMarker({
                      type: "car",
                      orderId: orderData.orderId,
                      position: {
                        lat: orderData.hubLocation.latitude,
                        lng: orderData.hubLocation.longitude,
                      },
                      title: `Auftrag ${orderData.orderId}`,
                      address: orderData.numberPlate
                        ? `Kennzeichen: ${orderData.numberPlate}`
                        : "Kennzeichen nicht verfügbar",
                    })
                  }}
                />
              )
            }

            // Phase 3, 6: Show live driver location
            if (orderData.orderPhase === 3 || orderData.orderPhase === 6) {
              if (!orderData.driverLocation) {
                console.log(
                  `[TrackingMapView] Order ${orderData.orderId} has no driver location yet`,
                )
                return null
              }

              // Validate driver location coordinates
              const { latitude, longitude } = orderData.driverLocation
              if (
                typeof latitude !== "number" ||
                typeof longitude !== "number" ||
                isNaN(latitude) ||
                isNaN(longitude)
              ) {
                console.warn(
                  `[TrackingMapView] Invalid driver location for order ${orderData.orderId}`,
                )
                return null
              }

              return (
                <SmoothCarMarker
                  key={orderData.orderId}
                  // PLA-278: HDP transporter only while outbound on phase 3;
                  // the return leg + phase 6 delivery show the customer's car.
                  spriteVariant={carSpriteVariant(
                    orderData.orderPhase,
                    orderData.driverLocation.isReturn ?? false,
                  )}
                  // FleetHub comet tail: feed this car's own per-order buffer,
                  // read by the matching CarTrailOverlay rendered below.
                  trail={getFleetTrail(orderData.orderId)}
                  location={{
                    latitude: orderData.driverLocation.latitude,
                    longitude: orderData.driverLocation.longitude,
                    timestamp: orderData.driverLocation.timestamp,
                    heading: orderData.driverLocation.heading || 0,
                    speed: orderData.driverLocation.speed || 0,
                  }}
                  onClick={() => {
                    setSelectedFleetHubMarker({
                      type: "car",
                      orderId: orderData.orderId,
                      position: {
                        lat: orderData.driverLocation.latitude,
                        lng: orderData.driverLocation.longitude,
                      },
                      title: `Auftrag ${orderData.orderId}`,
                      address: orderData.numberPlate
                        ? `Kennzeichen: ${orderData.numberPlate}`
                        : "Kennzeichen nicht verfügbar",
                    })
                  }}
                />
              )
            }

            // Phase 7 or other: No car shown
            return null
          })
        })()}

      {/* Multiple Order Comet Trails - one tilt-safe vector tail per live car.
          Each overlay reads the same per-order buffer its SmoothCarMarker feeds
          (getFleetTrail), and is mounted only for live-driving orders (phase
          3/6 with a position) — the multi-order mirror of the single-order
          `active={isLiveDriving}` gate. */}
      {showMultipleOrders &&
        activeOrders &&
        Array.from(activeOrders.values()).map((orderData: any) => {
          if (!orderData?.orderId || orderData.serviceType === "driveby") return null
          const isLiveOrder =
            (orderData.orderPhase === 3 || orderData.orderPhase === 6) && !!orderData.driverLocation
          if (!isLiveOrder) return null
          return (
            <CarTrailOverlay
              key={`trail-${orderData.orderId}`}
              trail={getFleetTrail(orderData.orderId)}
              active
            />
          )
        })}

      {/* Multiple Order Hub Markers - Show hub with waves for each order */}
      {showMultipleOrders &&
        activeOrders &&
        Array.from(activeOrders.values()).map((orderData: any) => {
          if (!orderData?.hubLocation) {
            console.log(`[TrackingMapView] Order ${orderData?.orderId} has no hub location`)
            return null
          }

          return (
            <AnimatedEndpointMarker
              key={`hub-${orderData.orderId}`}
              position={{
                lat: orderData.hubLocation.latitude,
                lng: orderData.hubLocation.longitude,
              }}
              waveColor={colors.primary}
              markerContent="delivery" // HDP logo
              showWaves={true} // Animated waves
              onClick={() => {
                setSelectedFleetHubMarker({
                  type: "hub",
                  orderId: orderData.orderId,
                  position: {
                    lat: orderData.hubLocation.latitude,
                    lng: orderData.hubLocation.longitude,
                  },
                  title: `HolDeinePlakette ${orderData.hubLocation.name || orderData.hubLocation.location || "Prüfzentrum"}`,
                  address: orderData.hubLocation.address || "",
                })
              }}
            />
          )
        })}

      {/* Multiple Order Customer Markers - Show customer location for each order */}
      {showMultipleOrders &&
        activeOrders &&
        Array.from(activeOrders.values()).map((orderData: any) => {
          if (!orderData?.customerLocation) {
            console.log(`[TrackingMapView] Order ${orderData?.orderId} has no customer location`)
            return null
          }

          // Skip for driveby orders
          if (orderData.serviceType === "driveby") return null

          return (
            <AnimatedEndpointMarker
              key={`customer-${orderData.orderId}`}
              position={{
                lat: orderData.customerLocation.latitude,
                lng: orderData.customerLocation.longitude,
              }}
              waveColor={colors.primary}
              markerContent="pickup" // Red dot
              showWaves={false}
              onClick={() => {
                setSelectedFleetHubMarker({
                  type: "hub",
                  orderId: orderData.orderId,
                  position: {
                    lat: orderData.customerLocation.latitude,
                    lng: orderData.customerLocation.longitude,
                  },
                  title: orderData.customerName || `Kundenstandort - Auftrag ${orderData.orderId}`,
                  address:
                    orderData.customerAddress ||
                    (orderData.numberPlate
                      ? `Kennzeichen: ${orderData.numberPlate}`
                      : "Adresse nicht verfügbar"),
                })
              }}
            />
          )
        })}

      {/* Multiple Order Route Lines - Show routes for all active orders */}
      {showMultipleOrders &&
        activeOrders &&
        Array.from(activeOrders.values()).map((orderData: any) => {
          // Validate orderData
          if (!orderData || !orderData.orderId) {
            console.warn("[TrackingMapView] Invalid orderData, skipping route")
            return null
          }

          if (!orderData.hubLocation || !orderData.customerLocation) return null

          // Validate hub location
          if (
            typeof orderData.hubLocation.latitude !== "number" ||
            typeof orderData.hubLocation.longitude !== "number" ||
            isNaN(orderData.hubLocation.latitude) ||
            isNaN(orderData.hubLocation.longitude)
          ) {
            console.warn(`[TrackingMapView] Invalid hub location for order ${orderData.orderId}`)
            return null
          }

          // Validate customer location
          if (
            typeof orderData.customerLocation.latitude !== "number" ||
            typeof orderData.customerLocation.longitude !== "number" ||
            isNaN(orderData.customerLocation.latitude) ||
            isNaN(orderData.customerLocation.longitude)
          ) {
            console.warn(
              `[TrackingMapView] Invalid customer location for order ${orderData.orderId}`,
            )
            return null
          }

          const origin = {
            latitude: orderData.hubLocation.latitude,
            longitude: orderData.hubLocation.longitude,
          }

          const destination = {
            latitude: orderData.customerLocation.latitude,
            longitude: orderData.customerLocation.longitude,
          }

          // Animate the route line if order is in live tracking phase (3 or 6)
          const shouldAnimate = orderData.orderPhase === 3 || orderData.orderPhase === 6

          // Phase 6 always goes hub → customer (return)
          // Phase 3 depends on isReturn status (pickup or return to hub)
          const isReturn =
            orderData.orderPhase === 6 ? false : orderData.driverLocation?.isReturn || false

          return (
            <AnimatedRouteLine
              key={`route-${orderData.orderId}`}
              origin={origin}
              destination={destination}
              showAnimation={shouldAnimate}
              isReturn={isReturn}
            />
          )
        })}

      {/* Regular markers - Only show when NOT showing all locations */}
      {!showAllLocations && (
        <>
          {/* Curved Route Line - Show on phases 1-6, hide on phase 7 - Hide for driveby */}
          {showRoute && orderPhase !== 7 && !isDriveby && (
            <>
              {/* AnimatedRouteLine props debugging removed for production */}
              <AnimatedRouteLine
                origin={_pickupLocation || null}
                destination={_customerLocation}
                showAnimation={orderPhase === 3 || orderPhase === 6} // Animate during live tracking
                isReturn={orderPhase === 6 ? false : orderPhase === 3 ? isDriverReturning : false} // Phase 6 always hub to customer, Phase 3 uses actual isReturn
                paused={carIsStale} // PLA-272: shimmer pauses while the GPS feed is stale
              />
            </>
          )}

          {/* PLA-273: Ignition Red comet-tail trail behind the driving car.
              Renders only while live-driving (the trail buffer is also only
              fed then — see the SmoothCarMarker `trail` wiring below).
              ADR-0033 follow-up: now a tilt-safe vector renderer (Polyline
              buckets), so it stays glued to the road through the full 3D city
              view — always on while live-driving, no tilt gate. */}
          <CarTrailOverlay trail={carTrailRef} active={isLiveDriving} />

          {/* Hub/Inspection Center Marker (HDP logo) - with wave effect.
              PLA-267 role swap: the halo crossfades out when the return leg
              starts (the customer pin takes over as the active destination). */}
          {showRoute && _pickupLocation && (
            <AnimatedEndpointMarker
              position={{ lat: _pickupLocation.latitude, lng: _pickupLocation.longitude }}
              waveColor={colors.primary}
              markerContent="delivery" // This shows the HDP logo
              showWaves={hubWavesVisible}
              // PLA-287: on the customer -> hub leg the hub is the active
              // destination, so its halo pulses with the approach intensity
              // (undefined elsewhere = legacy static look).
              pulseIntensity={hubPulseIntensity}
              animateWaveChanges={waveChangeAnimated}
              onClick={handleHubClick}
            />
          )}

          {/* Driver Car Marker - Phase-based display - Hide for driveby */}
          {(() => {
            // Hide driver for driveby service type
            if (isDriveby) return null

            // PLA-292: phase 7 keep-alive — render a ghost marker at the
            // customer/delivery location for ~1800ms so the confetti burst
            // has a DOM host. Only while justCompleted is true (set by the
            // outer shell on the 6→7 transition, cleared after the burst).
            if (orderPhase === 7) {
              if (justCompleted && _customerLocation) {
                return (
                  <SmoothCarMarker
                    location={_customerLocation}
                    staleAffordance={false}
                    spriteVariant="customer"
                    celebrationSignal={celebrationSignal}
                  />
                )
              }
              return null
            }

            // Phase 1, 2: Hide driver completely
            if (orderPhase === 1 || orderPhase === 2) return null

            // Phase 4, 5: Show the sprite car statically at hub.
            // PLA-272: onStaleChange stays wired here too — React reuses the
            // same SmoothCarMarker instance across the phase branches, so the
            // stale flag must keep flowing to the parent through every phase
            // or a stale flag set in phase 3 could outlive the live leg.
            // staleAffordance={false}: GPS silence is EXPECTED at the hub (no
            // live leg streams during inspection) — entering this phase also
            // clears any stale dim/pause carried over from the live leg.
            if (orderPhase === 4 || orderPhase === 5) {
              // Car should be at hub/pickup location, not customer location
              if (!_pickupLocation) {
                return null
              }
              return (
                <SmoothCarMarker
                  location={_pickupLocation}
                  onStaleChange={setCarIsStale}
                  staleAffordance={false}
                  // PLA-278: the car parked at the hub for inspection is the
                  // customer's own car (phase 4/5 -> "customer").
                  spriteVariant={carSpriteVariant(orderPhase, false)}
                  // PLA-292: scanner overlay while car is at hub for inspection.
                  scanning={true}
                />
              )
            }

            // Phase 3, 6: Show live driver location.
            // PLA-267: the docking signal plays the one-shot arrival settle.
            // PLA-272: staleness transitions pause the route shimmer above.
            // PLA-273: the marker feeds displayed poses into the comet-tail
            // buffer; wired only here, so static at-hub phases never buffer.
            if ((orderPhase === 3 || orderPhase === 6) && driverLocation) {
              return (
                <SmoothCarMarker
                  location={driverLocation}
                  dockingSignal={dockingSignal}
                  onStaleChange={setCarIsStale}
                  trail={isLiveDriving ? carTrailRef : undefined}
                  // PLA-278: HDP transporter only while outbound on phase 3;
                  // return leg + phase 6 delivery render the customer's car.
                  spriteVariant={carSpriteVariant(orderPhase, isDriverReturning ?? false)}
                />
              )
            }

            return null
          })()}

          {/* Customer Location Marker (red dot) - Hide for driveby.
              PLA-267: during live driving this is the approach destination —
              its halo pulse scales with the approach intensity (idle when far,
              energetic when arrived); undefined intensity = legacy look. */}
          {_customerLocation && !isDriveby && (
            <AnimatedEndpointMarker
              position={{ lat: _customerLocation.latitude, lng: _customerLocation.longitude }}
              waveColor={colors.primary}
              markerContent="pickup" // This shows the red dot
              showWaves={false}
              pulseIntensity={customerPulseIntensity}
              animateWaveChanges={waveChangeAnimated}
              onClick={handleCustomerClick}
            />
          )}

          {/* Info Windows */}
          {selectedMarker === "hub" &&
            _pickupLocation &&
            orderTrackingStore.currentOrder?.hubLocation && (
              <MarkerInfoWindow
                position={{ lat: _pickupLocation.latitude, lng: _pickupLocation.longitude }}
                title={`HolDeinePlakette ${orderTrackingStore.currentOrder.hubLocation.name || "Prüfzentrum"}`}
                address={orderTrackingStore.currentOrder.hubLocation.address || ""}
                onClose={() => setSelectedMarker(null)}
                openingHours={
                  allLocations.find(
                    (l) => l.location === orderTrackingStore.currentOrder?.hubLocation?.name,
                  )?.openingHours?.schedule
                }
                mapsUrl={orderTrackingStore.currentOrder.hubLocation.mapsUrl ?? undefined}
              />
            )}

          {selectedMarker === "customer" && _customerLocation && (
            <MarkerInfoWindow
              position={{ lat: _customerLocation.latitude, lng: _customerLocation.longitude }}
              title={orderTrackingStore.currentOrder?.customerName || "Kundenstandort"}
              address={
                _customerAddress || orderTrackingStore.currentOrder?.customerLocation?.address || ""
              }
              onClose={() => setSelectedMarker(null)}
            />
          )}
        </>
      )}

      {/* FleetHub Info Window - for multi-order tracking - Always available */}
      {selectedFleetHubMarker && (
        <MarkerInfoWindow
          position={selectedFleetHubMarker.position}
          title={selectedFleetHubMarker.title}
          address={selectedFleetHubMarker.address}
          onClose={() => setSelectedFleetHubMarker(null)}
        />
      )}
    </>
  )
}

// Responsive utilities
const useWindowDimensions = () => {
  const [dimensions, setDimensions] = useState({
    width: typeof window !== "undefined" ? window.innerWidth : 1200,
    height: typeof window !== "undefined" ? window.innerHeight : 800,
  })

  useEffect(() => {
    if (typeof window === "undefined") return

    const handleResize = () => {
      setDimensions({
        width: window.innerWidth,
        height: window.innerHeight,
      })
    }

    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  return dimensions
}

export const TrackingMapView: FC<TrackingMapViewProps> = (props) => {
  const {
    driverLocation: _driverLocation,
    customerLocation,
    customerAddress: _customerAddress,
    showRoute: _showRoute,
    orderPhase: _orderPhase = 1,
    pickupLocation,
    isDriverReturning: _isDriverReturning = false,
    isDriveby = false,
    onMapLoaded,
    allLocations: _allLocations = [],
    showAllLocations: _showAllLocations = false,
    onTransitionToOrder: _onTransitionToOrder,
    fleetHubCustomerLocations: _fleetHubCustomerLocations = [],
    bottomSheetHeight,
    headerHeight,
  } = props

  const { orderTrackingStore } = useStores()
  const [mapInstance, setMapInstance] = useState<google.maps.Map | null>(null)
  const [selectedMarker, setSelectedMarker] = useState<"hub" | "customer" | null>(null)
  const [centerBounds, setCenterBounds] = useState<google.maps.LatLngBounds | null>(null)
  const [centerPosition, setCenterPosition] = useState<{ lat: number; lng: number } | null>(null)
  const [centerZoom, setCenterZoom] = useState<number>(13)
  const isMounted = useRef(true)

  // PLA-292: completion confetti keep-alive. Tracks the previous orderPhase
  // so we can detect the exact 6→7 (or any non-7→7) transition and fire once.
  const prevOrderPhaseRef = useRef(_orderPhase)
  const [celebrationSignal, setCelebrationSignal] = useState(0)
  const [justCompleted, setJustCompleted] = useState(false)
  // Hold the reset timer in a ref (NOT the effect cleanup): if the phase
  // flickers out of 7 before 1800ms, an effect-cleanup clearTimeout would strand
  // justCompleted=true forever. The ref-held timer always fires and resets;
  // a fresh into-7 transition clears the prior one so timers never stack.
  const completionTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  useEffect(() => {
    const prev = prevOrderPhaseRef.current
    prevOrderPhaseRef.current = _orderPhase
    if (_orderPhase === 7 && prev !== 7) {
      setCelebrationSignal((s) => s + 1)
      setJustCompleted(true)
      if (completionTimerRef.current) clearTimeout(completionTimerRef.current)
      completionTimerRef.current = setTimeout(() => {
        if (isMounted.current) setJustCompleted(false)
      }, 1800)
    }
  }, [_orderPhase])

  // Clear the completion timer on unmount only.
  useEffect(() => () => clearTimeout(completionTimerRef.current), [])
  const dimensions = useWindowDimensions()
  const _isMobile = dimensions.width < 768

  // PLA-266: follow camera engages only while live-driving a single B2C order
  // (phases 3 = en route / 6 = return transport) with a known driver position.
  // The all-locations intro and driveby orders stay on plain overview; FleetHub
  // multi-order is pinned to overview inside the machine via `multiOrder`.
  const isLiveDriving =
    !_showAllLocations &&
    !isDriveby &&
    (_orderPhase === 3 || _orderPhase === 6) &&
    !!_driverLocation
  // PLA-287: which round-trip leg is being driven. On the customer -> hub
  // ("toHub") leg the driver is taking the customer's car to the inspection
  // centre after handover, so the HUB — not the customer pin — is the camera +
  // approach destination. leg and destination derive from one source so they
  // can never disagree. (See resolveTrackingLeg for the full truth table.)
  const trackingLeg = resolveTrackingLeg(_orderPhase, _isDriverReturning)
  const activeDestination = (trackingLeg === "toHub" ? pickupLocation : customerLocation) ?? null
  const followCamera = useFollowCamera({
    map: mapInstance,
    driverLocation: _driverLocation,
    destinationLocation: activeDestination,
    isLiveDriving,
    multiOrder: props.showMultipleOrders ?? false,
    obstructedBottomPx: bottomSheetHeight ?? 0,
  })

  // PLA-267/287: approach-moments choreography — single-order B2C only. FleetHub
  // multi-order and the all-locations intro never run moments; driveby has no
  // driving leg at all.
  //
  // Distance is to the ACTIVE destination (customer on pickup/return, hub on the
  // toHub leg) and is recomputed in-render from the SAME driver tick +
  // destination as `leg` — deliberately NOT reused from the follow camera. The
  // camera's exported distance is downstream React state that lags one render,
  // so on the exact render the leg flips (customer -> hub) the machine would
  // otherwise see the OLD destination's distance (a near-zero customer distance,
  // since the car is at the customer at handover) under leg="toHub" and latch the
  // hub pin to "arrived". Recomputing here keeps leg + distance consistent within
  // one render pass. The extra haversine per tick is negligible.
  const approachDistanceMeters =
    _driverLocation && activeDestination
      ? calculateDistance(
          { latitude: _driverLocation.latitude, longitude: _driverLocation.longitude },
          { latitude: activeDestination.latitude, longitude: activeDestination.longitude },
        )
      : null
  const approachEnabled = !_showAllLocations && !isDriveby && !(props.showMultipleOrders ?? false)
  const approach = useApproachMoments({
    distanceToDestinationMeters: approachDistanceMeters,
    isLiveDriving,
    leg: trackingLeg,
    enabled: approachEnabled,
  })
  // Visual choreography only runs during live-driving phases; outside them
  // every marker renders exactly as before (idle pin, hub halo on).
  const choreographyActive = approachEnabled && isLiveDriving
  // The active destination pin pulses with the approach intensity; the other
  // pin stays idle. Customer pin is the destination on pickup/return; the hub
  // pin takes over on the customer -> hub ("toHub") leg.
  const customerPulseIntensity =
    choreographyActive && approach.leg !== "toHub" ? approach.pulseIntensity : undefined
  const hubPulseIntensity =
    choreographyActive && approach.leg === "toHub" ? approach.pulseIntensity : undefined
  // Hub static waves are the "origin just departed" affordance — suppressed once
  // a pin takes over as the active approach destination (return → customer pin;
  // toHub → the hub pin pulses instead, via hubPulseIntensity).
  const hubWavesVisible = !(
    choreographyActive &&
    (approach.leg === "return" || approach.leg === "toHub")
  )
  const waveChangeAnimated = !approach.reducedMotion

  useEffect(() => {
    return () => {
      isMounted.current = false
    }
  }, [])

  // Log driver location updates
  useEffect(() => {
    // Driver location prop changed
    if (props.driverLocation) {
      // Driver location update received
    } else {
      // Driver location is null
    }
  }, [props.driverLocation])

  // Inject CSS to remove Google Maps default marker styling
  useEffect(() => {
    const style = document.createElement("style")
    style.textContent = `
      /* Remove default Google Maps marker styling */
      .gm-advanced-marker {
        background: transparent !important;
        border: none !important;
        box-shadow: none !important;
        border-radius: 0 !important;
      }
      .gm-advanced-marker > div {
        background: transparent !important;
        border: none !important;
        box-shadow: none !important;
        border-radius: 0 !important;
      }
      /* Smooth car marker (sprite sheet) styles to prevent flickering.
         Geometry (size + absolute centering on the GPS anchor) is owned by
         buildSpriteContainer in smooth-car-sprite.ts — never reintroduce
         position: relative here (PLA-274 zoom-drift anchor contract). */
      .smooth-car-marker {
        width: 60px;
        height: 60px;
        position: absolute;
        will-change: transform;
        backface-visibility: hidden;
        -webkit-backface-visibility: hidden;
        transform: translateZ(0);
        -webkit-transform: translateZ(0);
      }
      .smooth-car-marker > div {
        background-repeat: no-repeat;
      }
      /* Disable any Google Maps default transitions on the marker container.
         (Frame crossfades run on the child sprite layers, not the container.) */
      .gm-style .smooth-car-marker {
        transition: none !important;
      }
      /* Map container */
      .map-container {
        width: 100%;
        height: 100%;
      }
    `
    document.head.appendChild(style)
    return () => {
      document.head.removeChild(style)
    }
  }, [])

  const handleMapLoad = useCallback(
    (map: google.maps.Map) => {
      // console.log("[TrackingMapView] Map loaded successfully:", map)
      setMapInstance(map)

      // Store initial map center and bounds for recenter functionality
      if (pickupLocation || customerLocation) {
        const bounds = new google.maps.LatLngBounds()

        if (pickupLocation) {
          bounds.extend({ lat: pickupLocation.latitude, lng: pickupLocation.longitude })
        }

        if (customerLocation && !isDriveby) {
          bounds.extend({ lat: customerLocation.latitude, lng: customerLocation.longitude })
        }

        // Store for recenter button
        setCenterBounds(bounds)

        // If single location, store center and zoom
        const isSingleLocation =
          isDriveby ||
          (pickupLocation && !customerLocation) ||
          (!pickupLocation && customerLocation)

        if (isSingleLocation) {
          const loc = pickupLocation || customerLocation
          if (loc) {
            setCenterPosition({ lat: loc.latitude, lng: loc.longitude })
            setCenterZoom(14)
          }
        }
      }

      // Notify parent that map is loaded and ready
      onMapLoaded?.()
    },
    [pickupLocation, customerLocation, isDriveby, onMapLoaded],
  )

  // Calculate default center based on available locations
  const defaultCenter = useMemo(() => {
    // Try pickup location first
    if (pickupLocation) {
      return { lat: pickupLocation.latitude, lng: pickupLocation.longitude }
    }
    // Then customer location
    if (customerLocation) {
      return { lat: customerLocation.latitude, lng: customerLocation.longitude }
    }
    // Fallback to Germany center
    return { lat: 50.1109, lng: 8.6821 }
  }, [pickupLocation, customerLocation])

  // Recenter map to initial view
  const handleRecenter = useCallback(() => {
    if (!mapInstance) return

    if (centerBounds) {
      // Multiple locations - use fitBounds with simple padding
      const padding = getMapPadding(bottomSheetHeight, headerHeight)

      mapInstance.fitBounds(centerBounds, padding)
    } else if (centerPosition) {
      // Single location - use setCenter
      mapInstance.setCenter(centerPosition)
      mapInstance.setZoom(centerZoom)
    }
  }, [mapInstance, centerBounds, centerPosition, centerZoom, bottomSheetHeight, headerHeight])

  return (
    <View style={$container}>
      <APIProvider apiKey={GOOGLE_MAPS_API_KEY} version="weekly" libraries={["places", "marker"]}>
        <Map
          defaultCenter={defaultCenter}
          defaultZoom={13}
          mapId="a1cb8b51aa8a5eb6a6291ac0"
          gestureHandling="greedy"
          disableDefaultUI
          className="map-container"
        >
          <MapContent
            {...props}
            onMapLoad={handleMapLoad}
            orderTrackingStore={orderTrackingStore}
            selectedMarker={selectedMarker}
            setSelectedMarker={setSelectedMarker}
            setCenterBounds={setCenterBounds}
            centerBounds={centerBounds}
            isMobile={_isMobile}
            followCameraOwned={followCamera.cameraOwned}
            customerPulseIntensity={customerPulseIntensity}
            hubPulseIntensity={hubPulseIntensity}
            hubWavesVisible={hubWavesVisible}
            waveChangeAnimated={waveChangeAnimated}
            dockingSignal={approach.dockingSignal}
            isLiveDriving={isLiveDriving}
            celebrationSignal={celebrationSignal}
            justCompleted={justCompleted}
          />
        </Map>
      </APIProvider>

      {/* Recenter Button */}
      <TouchableOpacity style={$recenterButton} onPress={handleRecenter} activeOpacity={0.8}>
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill={colors.palette.neutral700}
          style={{ display: "block" }}
        >
          <path d="M12 8c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm8.94 3c-.46-4.17-3.77-7.48-7.94-7.94V1h-2v2.06C6.83 3.52 3.52 6.83 3.06 11H1v2h2.06c.46 4.17 3.77 7.48 7.94 7.94V23h2v-2.06c4.17-.46 7.48-3.77 7.94-7.94H23v-2h-2.06zM12 19c-3.87 0-7-3.13-7-7s3.13-7 7-7 7 3.13 7 7-3.13 7-7 7z" />
        </svg>
      </TouchableOpacity>
    </View>
  )
}

const $container: ViewStyle = {
  flex: 1,
  position: "relative",
  backgroundColor: colors.surface,
}

const $statusOverlay: ViewStyle = {
  position: "absolute",
  top: spacing.md,
  left: spacing.md,
  right: spacing.md,
  flexDirection: "row",
  alignItems: "flex-start",
  justifyContent: "space-between",
  pointerEvents: "none" as any,
}

const $statusBadge: ViewStyle = {
  backgroundColor: colors.surface,
  paddingHorizontal: spacing.md,
  paddingVertical: spacing.sm,
  borderRadius: 20,
  boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
  elevation: 2,
}

const $statusText: TextStyle = {
  fontSize: 14,
  fontWeight: "500",
  color: colors.text,
}

// const $statusBadgesContainer: ViewStyle = {
//   flexDirection: "row",
//   gap: spacing.sm,
//   marginLeft: "auto",
// }

// Dynamic style generators for responsive design
const _getStatusOverlayStyle = (isMobile: boolean): ViewStyle => ({
  ...$statusOverlay,
  top: isMobile ? spacing.sm : spacing.md,
  left: isMobile ? spacing.sm : spacing.md,
  right: isMobile ? spacing.sm : spacing.md,
})

const _getStatusBadgeStyle = (isMobile: boolean): ViewStyle => ({
  ...$statusBadge,
  paddingHorizontal: isMobile ? spacing.sm : spacing.md,
  paddingVertical: isMobile ? spacing.xs : spacing.sm,
})

const _getStatusTextStyle = (isMobile: boolean): TextStyle => ({
  ...$statusText,
  fontSize: isMobile ? 12 : 14,
})

// Recenter button styles - positioned top right, below the date
const $recenterButton: ViewStyle = {
  position: "absolute",
  top: 70, // Below the date/time display which is around 20-50px from top
  right: 20, // Same padding as other UI elements
  width: 40,
  height: 40,
  borderRadius: 20,
  backgroundColor: colors.surface,
  display: "flex" as any,
  justifyContent: "center",
  alignItems: "center",
  boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.15)",
  // @ts-ignore - Web-specific CSS property
  border: `1px solid ${colors.palette.neutral200}`,
  cursor: "pointer" as any,
  transition: "transform 0.2s ease" as any,
  zIndex: 1000,
}

// Add hover effect styles via CSS injection
if (typeof document !== "undefined") {
  const style = document.createElement("style")
  style.textContent = `
    .recenter-button:hover {
      transform: scale(1.03);
      box-shadow: 0px 3px 8px rgba(0, 0, 0, 0.2);
    }
  `
  document.head.appendChild(style)
}
